import { useEffect, useRef, type ReactNode } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  AlertCircle,
  XCircle,
  Users,
  CheckCircle2,
  ShieldAlert,
  Lightbulb,
  Info,
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { MeasureBar } from './HorizontalPerformanceChart'
import { MEASURE_DEFINITIONS } from '@/lib/equip-measure-definitions'
import { getMeasureDisplayName } from '@/lib/equip-utils'
import { useBreadcrumbs } from '@/views/equip/context/BreadcrumbContext'
import type { MeasureChartData } from '@/lib/equip-types'

interface MeasureAccordionItemProps {
  measure: MeasureChartData
  isExpanded: boolean
  isDrilldownLoading: boolean
  drilldownError: string | null
  onDismissError?: () => void
  onMeasureClick: (measureName: string, measureType: string) => void
  children?: ReactNode
}

export function MeasureAccordionItem({
  measure,
  isExpanded,
  isDrilldownLoading,
  drilldownError,
  onDismissError,
  onMeasureClick,
  children,
}: MeasureAccordionItemProps) {
  const { trail, addLevel, removeLevel } = useBreadcrumbs()
  const def = MEASURE_DEFINITIONS[measure.measureName]
  const isZeroDenom = measure.isZeroDenominator === true
  const remaining = Math.max(0, measure.eligible - measure.met)
  const pct = Math.round(measure.performance)
  // Config-driven display threshold (0-100) — tiered quarterly values flow through.
  const threshold = Math.round(measure.thresholdDisplay)
  const isPassing = measure.passThreshold
  const statLabels = def?.statLabels ?? ['Eligible', 'Complete', 'Remaining']
  const isOutcomeMeasure = ['quality', 'aco_visits', 'ed_sendout'].includes(measure.measureType)

  const breadcrumbId = `measure-${measure.measureName}`
  const headerRef = useRef<HTMLDivElement>(null)

  // Use a ref to read the latest trail value without including trail in
  // the dependency array. Including trail caused the effect to re-run every
  // time any breadcrumb changed, triggering removeLevel→addLevel cycles on
  // every measure click and causing DOM re-renders that swallowed events.
  const trailRef = useRef(trail)
  trailRef.current = trail

  // Stable ref for onMeasureClick so the effect closure always calls the
  // latest version without needing onMeasureClick as a dependency.
  const onMeasureClickRef = useRef(onMeasureClick)
  onMeasureClickRef.current = onMeasureClick

  // Breadcrumb registration: add/remove based on isExpanded
  // Depth: 1 if no parent clinician (standalone view), 2 if parent exists
  useEffect(() => {
    if (isExpanded) {
      const currentTrail = trailRef.current
      const hasParentClinician = currentTrail.some((level) => level.depth === 1)
      const depth = hasParentClinician ? 2 : 1

      addLevel({
        id: breadcrumbId,
        label: getMeasureDisplayName(measure.measureName),
        depth,
        onCollapse: () => onMeasureClickRef.current(measure.measureName, measure.measureType),
        scrollTarget: headerRef.current || undefined,
      })
    } else {
      removeLevel(breadcrumbId)
    }

    return () => {
      removeLevel(breadcrumbId)
    }
  }, [isExpanded, measure.measureName, measure.measureType, addLevel, removeLevel, breadcrumbId])

  // Color palette: blue for zero-denom, green for passing, red for failing
  // Dark mode uses bright saturated colors at high opacity
  const accent = isZeroDenom
    ? { ring: 'ring-slate-200/60 dark:ring-slate-700/60', text: 'text-blue-700 dark:text-blue-300', pctBg: 'bg-blue-100 dark:bg-blue-500/45', barBg: 'bg-blue-50 dark:bg-blue-500/20', gradientVar: 'var(--equip-gradient-zero)', hoverGlow: 'hover:shadow-[0_0_20px_rgba(59,130,246,0.5)]' }
    : isPassing
      ? { ring: 'ring-slate-200/60 dark:ring-slate-700/60', text: 'text-emerald-700 dark:text-emerald-300', pctBg: 'bg-emerald-100 dark:bg-teal-500/45', barBg: 'bg-green-50 dark:bg-teal-500/20', gradientVar: 'var(--equip-gradient-pass)', hoverGlow: 'hover:shadow-[0_0_20px_rgba(20,184,166,0.5)]' }
      : { ring: 'ring-slate-200/60 dark:ring-slate-700/60', text: 'text-rose-700 dark:text-red-300', pctBg: 'bg-rose-100 dark:bg-red-500/45', barBg: 'bg-red-50 dark:bg-red-500/20', gradientVar: 'var(--equip-gradient-fail)', hoverGlow: 'hover:shadow-[0_0_20px_rgba(239,68,68,0.5)]' }

  return (
    <motion.div
      className={cn(
        'rounded-xl transition-shadow duration-300',
        isExpanded
          ? `ring-1 ${accent.ring} shadow-md`
          : `ring-0 shadow-none border border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 ${accent.hoverGlow}`,
      )}
      animate={{
        backgroundColor: isExpanded ? undefined : 'transparent',
      }}
      style={isExpanded ? {} : { backgroundColor: 'transparent' }}
    >
      {/* Bar header — ref for breadcrumb scroll target */}
      <div ref={headerRef}>
        <MeasureBar
          measure={measure}
          isExpanded={isExpanded}
          onRowClick={onMeasureClick}
          accentBg={accent.barBg}
        />
      </div>

      {/* Expanded spotlight panel */}
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            key={`panel-${measure.measureName}`}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{
              height: { duration: 0.3, ease: [0.25, 0.1, 0.25, 1] },
              opacity: { duration: 0.2, delay: 0.05 },
            }}
            className="overflow-hidden rounded-b-xl"
            style={{ background: `linear-gradient(to bottom, ${accent.gradientVar} 0%, var(--background) 40%)` }}
          >
            <div className="px-5 pb-5 pt-1 space-y-5">

              {/* ── Hero stats row ── */}
              <div className="flex items-stretch gap-3">
                {/* Large percentage */}
                <div className={cn(
                  'flex flex-col items-center justify-center rounded-xl px-5 py-3 min-w-[90px]',
                  accent.pctBg,
                )}>
                  <span className={cn('text-3xl font-extrabold tabular-nums leading-none', accent.text)}>
                    {isZeroDenom ? 'N/A' : `${pct}%`}
                  </span>
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mt-1">
                    Rate
                  </span>
                </div>

                {/* Stat tiles */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 flex-1">
                  {!isZeroDenom && isOutcomeMeasure ? (
                    <TooltipProvider delayDuration={400}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="cursor-default">
                            <StatTile value={measure.eligible} label={statLabels[0]} variant="slate" />
                          </div>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-[260px]">
                          <p className="text-xs leading-relaxed">
                            Your eligible patient count may change throughout the quarter as patient assignments and admissions are updated. Check back regularly to make sure you're on track.
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ) : (
                    <StatTile value={measure.eligible} label={statLabels[0]} variant="slate" />
                  )}
                  <StatTile value={measure.met} label={statLabels[1]} variant="emerald" />
                  <StatTile value={remaining} label={statLabels[2]} variant="amber" />
                </div>

                {/* Threshold badge */}
                <div className="flex flex-col items-end justify-center gap-1 pl-2">
                  {isZeroDenom ? (
                    <Badge variant="lightInfo" className="text-xs">
                      Not Scored
                    </Badge>
                  ) : (
                    <Badge variant={isPassing ? 'lightSuccess' : 'lightError'} className="text-xs">
                      {isPassing ? 'Passing' : 'Below Threshold'}
                    </Badge>
                  )}
                  <span className="text-[11px] text-muted-foreground tabular-nums">
                    {threshold}% target
                  </span>
                </div>
              </div>

              {/* ── Zero-denom messaging panel ── */}
              {isZeroDenom && def && (
                <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50/60 dark:bg-blue-950/40 px-4 py-3">
                  <div className="flex items-start gap-2.5">
                    <Info className="h-4 w-4 text-blue-500 dark:text-blue-400 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-[13px] text-blue-800 dark:text-blue-300 leading-relaxed">
                        {def.zeroDenomMessage}
                      </p>
                      <p className="text-xs font-medium text-blue-600 dark:text-blue-400 mt-1.5">
                        You are passing this measure by default.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Measure info panel (non-zero-denom only) ── */}
              {!isZeroDenom && def && (
                <div className="rounded-lg border border-border bg-background divide-y divide-border mt-1">
                  {/* Summary */}
                  <div className="px-4 py-3">
                    <h4 className="text-base font-semibold text-foreground mb-1">
                      {def.displayName}
                    </h4>
                    <p className="text-[13px] text-muted-foreground leading-relaxed">
                      {def.summary}
                    </p>
                  </div>

                  {/* Eligibility + Met criteria — two-column layout */}
                  <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-border">
                    <InfoBlock
                      icon={<Users className="h-4 w-4 text-blue-500" />}
                      title="Who's Eligible"
                    >
                      {def.eligibility}
                    </InfoBlock>
                    <InfoBlock
                      icon={<CheckCircle2 className="h-4 w-4 text-emerald-500" />}
                      title="Counts as Met"
                    >
                      {def.metWhen}
                    </InfoBlock>
                  </div>

                  {/* Exclusions */}
                  {def.exclusions && def.exclusions.length > 0 && (
                    <div className="px-4 py-3">
                      <div className="flex items-start gap-2">
                        <ShieldAlert className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                        <div>
                          <span className="text-xs font-semibold text-muted-foreground">Exclusions: </span>
                          <span className="text-xs text-muted-foreground">
                            {def.exclusions.join(' \u00B7 ')}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Tips */}
                  {def.tips && (
                    <div className="px-4 py-3 bg-blue-50/40 dark:bg-blue-950/40">
                      <div className="flex items-start gap-2">
                        <Lightbulb className="h-4 w-4 text-blue-500 dark:text-blue-400 mt-0.5 shrink-0" />
                        <p className="text-xs text-blue-700 dark:text-blue-400 leading-relaxed">{def.tips}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ── Drilldown error ── */}
              {drilldownError && (
                <div className="flex items-center justify-between rounded-lg bg-destructive/5 border border-destructive/20 py-2.5 px-4">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-destructive shrink-0" />
                    <p className="text-sm text-destructive">{drilldownError}</p>
                  </div>
                  {onDismissError && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-destructive/70 hover:text-destructive"
                      onClick={onDismissError}
                    >
                      <XCircle className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              )}

              {/* ── Loading skeleton ── */}
              {isDrilldownLoading && !children && (
                <div className="space-y-2 pt-1">
                  <Skeleton className="h-5 w-40 mb-3" />
                  {[...Array(4)].map((_, i) => (
                    <Skeleton key={i} className="h-10 w-full rounded" />
                  ))}
                </div>
              )}

              {/* ── Grid content ── */}
              {children && (
                <div className="pt-1">
                  {children}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

/* ─── Sub-components ─── */

function StatTile({
  value,
  label,
  variant,
}: {
  value: number
  label: string
  variant: 'slate' | 'emerald' | 'amber'
}) {
  const bg = {
    slate: 'bg-slate-50 dark:bg-slate-400/25 border-slate-200 dark:border-slate-400/30',
    emerald: 'bg-emerald-50/70 dark:bg-teal-500/30 border-emerald-200 dark:border-teal-500/40',
    amber: 'bg-amber-50/70 dark:bg-amber-500/30 border-amber-200 dark:border-amber-500/40',
  }
  const text = {
    slate: 'text-foreground',
    emerald: 'text-emerald-700 dark:text-emerald-300',
    amber: 'text-amber-700 dark:text-amber-300',
  }
  return (
    <div className={cn('rounded-lg border px-3 py-2.5 flex flex-col items-center justify-center', bg[variant])}>
      <p className={cn('text-xl font-bold tabular-nums leading-none', text[variant])}>{value}</p>
      <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mt-1.5">
        {label}
      </p>
    </div>
  )
}

function InfoBlock({
  icon,
  title,
  children,
}: {
  icon: ReactNode
  title: string
  children: string
}) {
  return (
    <div className="px-4 py-3">
      <div className="flex items-start gap-2.5">
        <div className="mt-0.5 shrink-0">{icon}</div>
        <div>
          <p className="text-xs font-semibold text-muted-foreground mb-0.5">{title}</p>
          <p className="text-[13px] text-muted-foreground leading-relaxed">{children}</p>
        </div>
      </div>
    </div>
  )
}
